<template>
  <div class="availability-page">
    <div class="page-header">
      <h2>{{ $t('coachSchedule.title') }}</h2>
      <div class="status-filter">
        <el-radio-group v-model="statusFilter" size="medium" @change="filterByStatus">
          <el-radio-button label="全部">{{ $t('common.all') }}</el-radio-button>
          <el-radio-button label="空闲">{{ $t('coachSchedule.free') }}</el-radio-button>
          <el-radio-button label="预约中">{{ $t('coachSchedule.booked') }}</el-radio-button>
          <el-radio-button label="服务中">{{ $t('coachSchedule.serving') }}</el-radio-button>
          <el-radio-button label="待上课">{{ $t('coachSchedule.pendingClass') }}</el-radio-button>
          <el-radio-button label="待服务">{{ $t('coachSchedule.pendingService') }}</el-radio-button>
          <el-radio-button label="休息">{{ $t('coachSchedule.resting') }}</el-radio-button>
        </el-radio-group>
      </div>
    </div>

    <!-- 测试数据提示 -->
    <el-alert
      v-if="isUsingTestData"
      :title="$t('coachSchedule.testDataWarning')"
      type="warning"
      :closable="false"
      class="test-data-alert"
      show-icon>
    </el-alert>

    <!-- 功能操作区 -->
    <div class="actions">
      <!-- <el-button type="primary" @click="showAddDialog = true" icon="el-icon-plus">添加可用时间</el-button> -->
      <el-button type="success" @click="goBatchAdd" icon="el-icon-s-order">{{ $t('coachSchedule.batchAddAvailability') }}</el-button>
      <div class="search-box">
        <el-input 
          :placeholder="$t('coachSchedule.searchPlaceholder')" 
          v-model="searchInput" 
          class="search-input"
          prefix-icon="el-icon-search"
          clearable
          @clear="refreshCoaches"
          @keyup.enter.native="searchCoaches">
        </el-input>
        <el-button type="primary" @click="searchCoaches">{{ $t('common.search') }}</el-button>
      </div>
    </div>

    <!-- 教练卡片展示区 -->
    <div v-loading="coachesLoading" class="coach-cards">
      <el-row :gutter="20">
        <el-col 
          :xs="24" :sm="12" :md="8" :lg="6" :xl="4" 
          v-for="coach in filteredCoaches" 
          :key="coach.id">
          <el-card 
            shadow="hover" 
            class="coach-card"
            :class="[getStatusClass(coach.status), {'test-data': coach.isTestData}]"
            @click.native="selectCoach(coach)">
            <div class="card-content">
              <!-- 左侧布局：头像和基本信息 -->
              <div class="card-left">
                <div class="coach-avatar">
                  <img :src="coach.avatar || defaultAvatar" alt="教练头像" @error="handleImageError">
                  <div class="status-badge" :class="'status-' + coach.status">{{ coach.status }}</div>
                  <div v-if="coach.isTestData" class="test-badge">{{ $t('coachSchedule.test') }}</div>
                </div>
                <div class="coach-info">
                  <div class="coach-name">{{ coach.name }}</div>
                  <div class="coach-id">{{ $t('coachSchedule.coachId') }}: {{ coach.id }}</div>
                  <div class="coach-level">{{ $t('coachSchedule.level') }}: {{ coach.level }}</div>
                  <div class="availability-info">
                    <i class="el-icon-time"></i>
                    <span>{{ coach.availabilityCount || 0 }}{{ $t('coachSchedule.availableSlots') }}</span>
                  </div>
                </div>
              </div>
              
              <!-- 右侧布局：活动信息 -->
              <div class="card-right" v-if="['预约中', '服务中', '待上课', '待服务'].includes(coach.status)">
                <div class="activity-info">
                  <div class="activity-header">
                    <span class="activity-title">{{ getActivityTitle(coach.status) }}</span>
                    <span v-if="coach.status.includes('待')" class="countdown-indicator"></span>
                  </div>
                  <div class="activity-details" v-if="coachCurrentActivities[coach.id]">
                    <p v-if="coachCurrentActivities[coach.id].projectName" class="activity-item">
                      <i class="el-icon-s-order"></i> {{ coachCurrentActivities[coach.id].projectName }}
                    </p>
                    <p v-if="coachCurrentActivities[coach.id].startTime" class="activity-item">
                      <i class="el-icon-time"></i> 
                      {{ formatTime(coachCurrentActivities[coach.id].startTime) }}
                      <template v-if="coachCurrentActivities[coach.id].endTime">
                        - {{ formatTime(coachCurrentActivities[coach.id].endTime) }}
                      </template>
                    </p>
                    <p v-if="coach.status.includes('待') && coachCurrentActivities[coach.id].countdown" class="activity-item countdown">
                      <i class="el-icon-alarm-clock"></i> 还有 {{ coachCurrentActivities[coach.id].countdown }} 分钟
                    </p>
                  </div>
                </div>
              </div>
              <div class="card-right empty-activity" v-else>
                <div class="no-activity">
                  <i class="el-icon-s-release"></i>
                  <p>{{ coach.status === '空闲' ? $t('coachSchedule.canBook') : (coach.status === '休息' ? $t('coachSchedule.resting') : $t('coachSchedule.noActivity')) }}</p>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 无数据提示 -->
    <el-empty v-if="filteredCoaches.length === 0" :description="$t('coachSchedule.noCoachFound')"></el-empty>

    <!-- 分页 -->
    <el-pagination
      background
      layout="prev, pager, next, sizes, total"
      :total="totalCoaches"
      :page-size="pageSize"
      :page-sizes="[12, 24, 36, 48]"
      :current-page.sync="currentPage"
      @current-change="handlePageChange"
      @size-change="handleSizeChange"
      class="pagination">
    </el-pagination>

    <!-- 教练空闲时间详情对话框 -->
    <el-dialog 
      :title="selectedCoach.name + $t('coachDetail.avaiTime')"
      :visible.sync="showTimeDetailsDialog"
      width="70%"
      @closed="refreshCoaches">
      <div class="time-management-header">
        <div class="coach-basic-info">
          <img :src="selectedCoach.avatar || defaultAvatar" alt="教练头像" class="dialog-avatar" @error="handleImageError">
          <div>
            <h3>
              {{ selectedCoach.name }}
              <el-tag v-if="selectedCoach.isTestData" size="mini" type="warning">{{ $t('coachDetail.testData') }}</el-tag>
              <el-tag :type="getStatusTagType(selectedCoach.status)" size="mini">{{ selectedCoach.status }}</el-tag>
            </h3>
            <p>{{ $t('coachDetail.coachNumber') }}: {{ selectedCoach.id }} | {{ $t('coachDetail.level') }}: {{ selectedCoach.level }}</p>
          </div>
        </div>
        <div class="coach-actions">
        <el-button type="success" size="small" @click="addTimeForCoach" icon="el-icon-plus">{{ $t('coachDetail.addAvailableTime') }}</el-button>
          <el-button type="primary" size="small" @click="openBookDialog" icon="el-icon-s-order">{{ $t('coachDetail.bookCoach') }}</el-button>
        </div>
      </div>
      
      <!-- 添加当前活动详情 -->
      <div v-if="coachCurrentActivities[selectedCoach.id] && ['预约中', '服务中', '待上课', '待服务'].includes(selectedCoach.status)" class="current-activity-panel">
        <el-alert
          :title="getActivityTitle(selectedCoach.status)"
          :type="getActivityAlertType(selectedCoach.status)"
          :closable="false"
          show-icon>
        </el-alert>
        <el-descriptions border :column="2" class="activity-details-table">
          <el-descriptions-item :label="$t('coachDetail.activityType')">
            {{ selectedCoach.status.includes('预约') ? $t('coachDetail.appointment') : $t('coachDetail.service') }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('coachDetail.project')">
            {{ coachCurrentActivities[selectedCoach.id].projectName || $t('coachDetail.unknownProject') }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('coachDetail.startTime')">
            {{ formatDateTime(coachCurrentActivities[selectedCoach.id].startTime) }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('coachDetail.endTime')">
            {{ formatDateTime(coachCurrentActivities[selectedCoach.id].endTime) }}
          </el-descriptions-item>
          <el-descriptions-item v-if="coachCurrentActivities[selectedCoach.id].customerName" :label="$t('coachDetail.customer')">
            {{ coachCurrentActivities[selectedCoach.id].customerName }}
          </el-descriptions-item>
          <el-descriptions-item v-if="coachCurrentActivities[selectedCoach.id].remark" :label="$t('coachDetail.remark')" :span="2">
            {{ coachCurrentActivities[selectedCoach.id].remark }}
          </el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 教练空闲时间日历视图 -->
      <div class="calendar-view">
        <div class="calendar-header">
          <el-radio-group v-model="timeViewMode" size="small">
            <el-radio-button label="list">{{ $t('coachDetail.listView') }}</el-radio-button>
            <el-radio-button label="calendar">{{ $t('coachDetail.calendarView') }}</el-radio-button>
          </el-radio-group>
        </div>
        
        <!-- 日历视图 (仅在timeViewMode为calendar时显示) -->
        <div v-if="timeViewMode === 'calendar'" class="full-calendar">
          <!-- 使用FullCalendar或el-calendar组件 -->
          <el-calendar v-model="calendarDate">
            <template slot="dateCell" slot-scope="{date, data}">
              <div class="calendar-date-cell">
                <div class="calendar-day">{{ data.day.split('-').slice(2).join('-') }}</div>
                <div class="time-slots">
                  <div 
                    v-for="(slot, index) in getTimeSlotsForDate(date)" 
                    :key="index"
                    class="time-slot"
                    :class="{ 'test-data': slot.isTestData }">
                    {{ formatTimeSlot(slot) }}
                  </div>
                </div>
              </div>
            </template>
          </el-calendar>
        </div>
        
        <!-- 列表视图 (默认) -->
        <div v-else class="time-list">
          <el-table 
            :data="coachTimeSlots" 
            border 
            style="width: 100%"
            v-loading="timeSlotsLoading">
            <el-table-column prop="id" label="ID" width="80"></el-table-column>
            <el-table-column :label="$t('paymentRecord.date')" width="120">
              <template slot-scope="scope">
                {{ formatDate(scope.row.availableFrom) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('booking.time')" min-width="180">
              <template slot-scope="scope">
                {{ formatTime(scope.row.availableFrom) }} - {{ formatTime(scope.row.availableUntil) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('booking.status')" width="100">
              <template slot-scope="scope">
                <el-tag :type="getSlotStatusType(scope.row)">
                  {{ getSlotStatus(scope.row) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" width="180" align="center">
              <template slot-scope="scope">
                <el-button 
                  size="mini" 
                  type="primary" 
                  icon="el-icon-edit"
                  @click.stop="editTimeSlot(scope.row)"
                  :disabled="isTimeSlotBooked(scope.row)">
                  {{$t('common.edit')}}
                </el-button>
                <el-button 
                  size="mini" 
                  type="danger" 
                  icon="el-icon-delete"
                  @click.stop="deleteTimeSlot(scope.row.id)"
                  :disabled="isTimeSlotBooked(scope.row)">
                  {{$t('common.delete')}}
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </el-dialog>

    <!-- 添加/修改时间段对话框 -->
    <el-dialog 
      :title="isEditing ? $t('coachDetail.modifyAvailableTime') : $t('coachDetail.addAvailableTime')" 
      :visible.sync="showAddDialog"
      width="40%">
      <el-form :model="form" ref="form" label-width="100px" :rules="formRules">
        <el-form-item :label="$t('coachDetail.coachNumber')" prop="coachId">
          <el-select 
            v-model="form.coachId" 
            filterable 
            :placeholder="$t('coachSchedule.selectCoach')"
            :disabled="isEditing || !!selectedCoach.id"
            style="width: 100%">
            <el-option
              v-for="coach in coachesList"
              :key="coach.id"
              :label="`${coach.name} (${coach.id})`"
              :value="coach.id">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('coachDetail.startTime')" prop="startDateTime">
          <el-date-picker
            v-model="form.startDateTime"
            type="datetime"
            :placeholder="$t('coachDetail.selectStartTime')"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%">
          </el-date-picker>
        </el-form-item>
        <el-form-item :label="$t('coachDetail.endTime')" prop="endDateTime">
          <el-date-picker
            v-model="form.endDateTime"
            type="datetime"
            :placeholder="$t('coachDetail.selectEndTime')"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%">
          </el-date-picker>
        </el-form-item>
        <el-form-item label="快速设置">
          <div class="quick-time-buttons">
            <el-button size="mini" @click="setTimeRange('08:00-12:00')">上午段</el-button>
            <el-button size="mini" @click="setTimeRange('13:00-17:00')">下午段</el-button>
            <el-button size="mini" @click="setTimeRange('18:00-22:00')">晚上段</el-button>
            <el-button size="mini" @click="setTimeRange('08:00-22:00')">全天</el-button>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showAddDialog = false">取消</el-button>
        <el-button type="primary" @click="saveAvailability" :loading="submitting">保存</el-button>
      </span>
    </el-dialog>

    <!-- 预约教练对话框 -->
    <el-dialog
      :title="`${$t('coachDetail.bookCoachTitle')}: ${selectedCoach.name || ''}`"
      :visible.sync="showBookDialog"
      width="60%"
      @closed="resetBookForm">
      <el-form :model="bookForm" ref="bookForm" label-width="100px" :rules="bookRules">
        <!-- 客户信息部分 -->
        <el-divider content-position="left">{{ $t('coachDetail.customerInfo') }}</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('coachDetail.customerType')" prop="customerType">
              <el-radio-group v-model="bookForm.customerType" @change="customerTypeChange">
                <el-radio :label="1">{{ $t('coachDetail.walkInCustomer') }}</el-radio>
                <el-radio :label="2">{{ $t('coachDetail.memberCustomer') }}</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        
        <!-- 散客信息 -->
        <div v-if="bookForm.customerType === 1">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="姓名" prop="name">
                <el-input v-model="bookForm.name" placeholder="请输入散客姓名"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="手机号" prop="phone">
                <el-input v-model="bookForm.phone" placeholder="请输入散客手机号"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
  </div>
        
        <!-- 会员信息 -->
        <div v-if="bookForm.customerType === 2">
          <el-row :gutter="20">
            <el-col :span="18">
              <el-form-item label="会员搜索" prop="memberId">
                <el-input 
                  v-model="searchKey" 
                  placeholder="请输入会员账号/姓名/手机号"
                  @keyup.enter.native="searchMembers">
                  <el-button slot="append" icon="el-icon-search" @click="searchMembers"></el-button>
                </el-input>
              </el-form-item>
            </el-col>
          </el-row>
          
          <!-- 会员列表 -->
          <el-table
            v-if="members.length > 0"
            :data="members"
            border
            style="width: 100%; margin-bottom: 20px"
            @row-click="selectMember">
            <el-table-column prop="zhanghao" label="账号" width="120"></el-table-column>
            <el-table-column prop="xingming" label="姓名" width="120"></el-table-column>
            <el-table-column prop="shouji" label="手机号" width="150"></el-table-column>
            <el-table-column prop="yue" label="账户余额"></el-table-column>
            <el-table-column prop="viplevel" label="会员等级"></el-table-column>
            <el-table-column fixed="right" label="操作" width="100">
              <template slot-scope="scope">
                <el-button type="text" size="small" @click.stop="selectMember(scope.row)">选择</el-button>
              </template>
            </el-table-column>
          </el-table>
          
          <!-- 已选会员信息 -->
          <div v-if="selectedMember" class="selected-member">
            <el-alert
              title="已选会员"
              type="success"
              :closable="false">
              <template slot="title">
                已选会员：{{selectedMember.xingming}} ({{selectedMember.zhanghao}}) - 余额：¥{{selectedMember.yue}}
              </template>
            </el-alert>
          </div>
        </div>
        
        <!-- 预约信息部分 -->
        <el-divider content-position="left">预约信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('payment.selectProject')" prop="projectId">
              <el-select 
                v-model="bookForm.projectId" 
                filterable 
                :placeholder="$t('coachDetail.selectFitnessProject')"
                @change="projectChange">
                <el-option
                  v-for="item in projects"
                  :key="item.id"
                  :label="item.xiangmumingcheng"
                  :value="item.id">
                </el-option>
                <el-option key="other" :label="$t('coachDetail.otherProject')" value="other"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12" v-if="bookForm.projectId === 'other'">
            <el-form-item :label="$t('coachDetail.projectName')" prop="customProjectName">
              <el-input v-model="bookForm.customProjectName" :placeholder="$t('coachDetail.inputCustomProjectName')"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="服务时长" prop="duration">
              <el-input-number 
                v-model="bookForm.duration" 
                :min="1" 
                :step="15"
                controls-position="right"
                @change="updateEndTime"
                placeholder="服务时长（分钟）">
              </el-input-number>
              <span class="unit">分钟</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="服务价格" prop="price">
              <el-input-number 
                v-model="bookForm.price" 
                :min="0" 
                :precision="2"
                :step="10"
                controls-position="right"
                placeholder="服务价格">
              </el-input-number>
              <span class="unit">元</span>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="预约说明" prop="remark">
              <el-input v-model="bookForm.remark" placeholder="请输入预约说明"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('coachDetail.startTime')" prop="startTime">
              <el-date-picker
                v-model="bookForm.startTime"
                type="datetime"
                :placeholder="$t('coachDetail.selectAppointmentStartTime')"
                value-format="yyyy-MM-dd HH:mm:ss"
                format="yyyy-MM-dd HH:mm:ss"
                @change="updateEndTime">
              </el-date-picker>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('coachDetail.endTime')" prop="endTime">
              <el-date-picker
                v-model="bookForm.endTime"
                type="datetime"
                :placeholder="$t('coachDetail.selectAppointmentEndTime')"
                value-format="yyyy-MM-dd HH:mm:ss"
                format="yyyy-MM-dd HH:mm:ss">
              </el-date-picker>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="支付方式" prop="paymentMethod">
              <el-radio-group v-model="bookForm.paymentMethod">
                <el-radio :label="1">现金支付</el-radio>
                <el-radio :label="2" :disabled="bookForm.customerType !== 2">会员余额</el-radio>
                <el-radio :label="3">微信支付</el-radio>
                <el-radio :label="4">支付宝</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showBookDialog = false">取 消</el-button>
        <el-button type="primary" @click="submitBooking">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  data() {
    return {
      // 教练列表相关
      coachesList: [],
      coachesLoading: false, 
      filteredCoaches: [],
      totalCoaches: 0,
      currentPage: 1,
      pageSize: 12,
      statusFilter: '全部',
      searchInput: '',
      defaultAvatar: '',
      isUsingTestData: false, // 是否使用测试数据的标记
      
      // 新增：存储每个教练的当前活动信息
      coachCurrentActivities: {},
      
      // 选中的教练和时间槽
      selectedCoach: {},
      coachTimeSlots: [],
      timeSlotsLoading: false,
      showTimeDetailsDialog: false,
      timeViewMode: 'list',
      calendarDate: new Date(),
      
      // 表单相关
      showAddDialog: false,
      isEditing: false,
      submitting: false,
      form: this.initFormData(),
      formRules: {
        coachId: [
          { required: true, message: '请选择教练', trigger: 'change' }
        ],
        startDateTime: [
          { required: true, message: '请选择开始时间', trigger: 'change' }
        ],
        endDateTime: [
          { required: true, message: '请选择结束时间', trigger: 'change' }
        ]
      },
      showBookDialog: false,
      bookForm: {
        customerType: 1, // 1: 散客 2: 会员
        name: '',
        phone: '',
        memberId: null,
        projectId: null,
        customProjectName: '',
        duration: 60,
        price: 0, // 添加价格字段
        remark: '',
        startTime: null,
        endTime: null,
        paymentMethod: 1 // 添加支付方式：1-现金, 2-会员余额, 3-微信, 4-支付宝
      },
      bookRules: {
        customerType: [{ required: true, message: '请选择客户类型', trigger: 'change' }],
        name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3456789]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ],
        memberId: [{ required: true, message: '请选择会员', trigger: 'change' }],
        projectId: [{ required: true, message: '请选择健身项目', trigger: 'change' }],
        customProjectName: [{ required: true, message: '请输入自定义项目名称', trigger: 'blur' }],
        duration: [{ required: true, message: '请输入服务时长', trigger: 'blur' }],
        price: [{ required: true, message: '请输入服务价格', trigger: 'blur' }],
        startTime: [{ required: true, message: '请选择开始时间', trigger: 'change' }],
        endTime: [{ required: true, message: '请选择结束时间', trigger: 'change' }]
      },
      searchKey: '',
      members: [],
      selectedMember: null,
      projects: [],
    };
  },
  created() {
    try {
      // 尝试获取默认头像图片
      this.defaultAvatar = require('@/assets/img/default-avatar.png');
    } catch (e) {
      // 如果默认头像不存在，使用Element UI默认头像
      console.warn('默认头像加载失败，使用备用头像');
      this.defaultAvatar = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png';
    }
    this.fetchCoaches();
  },
  methods: {
    // 跳转到批量添加教练空闲时间页面
    goBatchAdd() {
      this.$router.push('/batchAddAvailability');
    },
    
    // 初始化表单数据
    initFormData() {
      return {
        id: null,
        coachId: '',
        startDateTime: '',
        endDateTime: '',
      };
    },
    
    // 获取教练列表
    fetchCoaches() {
      this.coachesLoading = true;
      this.$http.get('/jiaolian/page', {
        params: {
          page: this.currentPage,
          limit: this.pageSize,
          sort: "id"
        }
      })
      .then(response => {
        if (response.data && response.data.code === 0) {
          // 修复API返回结构路径，从data.data.list获取教练列表
          const coaches = response.data.data?.list || [];
          console.log('接收到的教练数据:', response.data);
          
          if (coaches.length === 0) {
            this.$message.warning('未获取到教练数据');
          }
          
          // 为每个教练添加状态信息和空闲时间数量
          this.coachesList = coaches.map(coach => {
            // 安全地获取属性值，防止某些属性不存在导致的错误
            return {
              id: coach.jiaolianbianhao || '未知编号',
              numericId: coach.id, // 存储数字ID
              name: coach.jiaolianxingming || '未知姓名',
              avatar: coach.touxiang ? this.$base.imgurl + coach.touxiang.split(',')[0] : this.defaultAvatar,
              level: coach.jiaoliandengji || '无等级',
              status: '加载中...', // 初始状态，后续通过API获取
              availabilityCount: 0, // 初始化为0，后续更新
              // 添加原始数据以便后续使用
              originalData: coach
            };
          });
          
          // 总记录数也从data.data.total获取
          this.totalCoaches = response.data.data?.total || 0;
          this.filteredCoaches = [...this.coachesList];
          
          // 获取教练状态信息和空闲时间数量
          this.updateCoachesStatus();
          this.updateCoachesAvailabilityCounts();
        } else {
          this.$message.error('获取教练列表失败：' + (response.data?.msg || '未知错误'));
          // 创建一些测试数据，以便在API失败时仍能展示界面
          this.generateTestCoaches();
        }
      })
      .catch(error => {
        console.error('获取教练列表错误:', error);
        this.$message.error('获取教练列表出错：' + (error.message || '网络错误'));
        // 创建一些测试数据，以便在API失败时仍能展示界面
        this.generateTestCoaches();
      })
      .finally(() => {
        this.coachesLoading = false;
      });
    },
    
    // 生成测试教练数据（在API失败时使用）
    generateTestCoaches() {
      // 设置标记，表示当前使用测试数据
      this.isUsingTestData = true;
      
      const statusOptions = ['空闲', '预约中', '服务中', '待上课', '待服务', '休息'];
      const levelOptions = ['初级教练', '中级教练', '高级教练', '资深教练'];
      const testCoaches = [];
      
      // 生成10个测试教练
      for (let i = 1; i <= 10; i++) {
        testCoaches.push({
          id: 'TEST' + i.toString().padStart(3, '0'),
          name: '测试教练' + i,
          avatar: this.defaultAvatar,
          level: levelOptions[Math.floor(Math.random() * levelOptions.length)],
          status: statusOptions[Math.floor(Math.random() * statusOptions.length)],
          availabilityCount: Math.floor(Math.random() * 10),
          isTestData: true // 标记为测试数据
        });
      }
      
      this.coachesList = testCoaches;
      this.filteredCoaches = [...testCoaches];
      this.totalCoaches = testCoaches.length;
    },
    
    // 更新教练状态信息
    updateCoachesStatus() {
      // 创建ID映射关系，用于后续正确识别教练
      const coachIdMap = new Map();
      
      // 记录所有可能的教练ID格式
      this.coachesList.forEach(coach => {
        // 教练编号作为主键
        coachIdMap.set(coach.id, coach.id);
        
        // 如果存在数字ID，也添加映射
        if (coach.numericId) {
          coachIdMap.set(coach.numericId.toString(), coach.id);
          coachIdMap.set(Number(coach.numericId), coach.id);
        }
        
        // 如果ID是数字字符串，也添加对应的数字类型
        if (!isNaN(Number(coach.id))) {
          coachIdMap.set(Number(coach.id), coach.id);
        }
      });
      
      console.log('教练ID映射:', Object.fromEntries(coachIdMap));
      
      // 初始化状态映射和活动信息
      const statusMap = {};
      const activityMap = {};
      
      this.coachesList.forEach(coach => {
        statusMap[coach.id] = '空闲';
        activityMap[coach.id] = null;
      });
      
      // 获取当前时间，用于比较
      const now = new Date();
      console.log('当前时间:', now.toISOString());
      
      // 使用Promise.all并行获取所有需要的数据
      Promise.all([
        // 获取教练预约信息
        this.$http.get('/jiaolianyuyue/page', {
          params: {
            page: 1,
            limit: 1000, // 获取较大数量记录以确保覆盖所有教练
            sfsh: '是' // 只获取已审核的预约
          }
        }),
        
        // 获取支付记录信息
        this.$http.get('/paymentrecord/page', {
          params: {
            page: 1,
            limit: 1000 // 获取较大数量记录以确保覆盖所有教练
          }
        }),
        
        // 获取空闲时间信息
        this.$http.get('/schedule/getAllAvailability')
      ])
      .then(([appointmentsResponse, paymentsResponse, availabilityResponse]) => {
        console.log('教练列表:', this.coachesList);
        
        // 处理教练预约数据
        if (appointmentsResponse.data && appointmentsResponse.data.code === 0) {
          const appointments = appointmentsResponse.data.data?.list || [];
          console.log('获取到的预约数据:', appointments);
          
          appointments.forEach(appointment => {
            const rawCoachId = appointment.jiaolianbianhao;
            if (!rawCoachId) return;
            
            // 使用ID映射找到标准化的教练ID
            const coachId = coachIdMap.get(rawCoachId) || rawCoachId;
            
            // 检查预约时间是否在当前时间点
            try {
              // 确保我们从预约对象中获取正确的开始和结束时间字段
              const startTimeStr = appointment.startTime;
              const endTimeStr = appointment.endTime;
              
              console.log(`预约时间检查 - 教练ID: ${coachId}, 开始时间: ${startTimeStr}, 结束时间: ${endTimeStr}`);
              
              if (!startTimeStr || !endTimeStr) {
                console.warn(`教练${coachId}的预约时间字段为空`);
                return;
              }
              
              const startTime = new Date(startTimeStr);
              const endTime = new Date(endTimeStr);
              
              console.log(`预约时间解析结果 - 开始: ${startTime.toISOString()}, 结束: ${endTime.toISOString()}`);
              console.log(`预约时间比较 - 开始<=现在? ${startTime <= now}, 结束>=现在? ${endTime >= now}`);
              
              if (!isNaN(startTime.getTime()) && !isNaN(endTime.getTime())) {
                // 检查预约是否当前正在进行中
                if (startTime <= now && endTime >= now) {
                  // 当前正在上课
                  statusMap[coachId] = '预约中';
                  
                  // 记录当前预约信息
                  activityMap[coachId] = {
                    type: '预约',
                    startTime: startTime,
                    endTime: endTime,
                    projectName: appointment.xiangmumingcheng || '未知项目',
                    customerName: appointment.xingming,
                    remark: appointment.yuyueshuoming
                  };
                  
                  console.log(`教练${coachId}当前预约中, ${startTime.toISOString()} - ${endTime.toISOString()}`);
                } 
                // 检查是否将在30分钟内开始
                else if (startTime > now && (startTime.getTime() - now.getTime()) < 30 * 60 * 1000) {
                  // 30分钟内将要上课
                  statusMap[coachId] = '待上课';
                  
                  // 记录即将开始的预约信息
                  const countdown = Math.floor((startTime.getTime() - now.getTime()) / 60000);
                  activityMap[coachId] = {
                    type: '预约',
                    startTime: startTime,
                    endTime: endTime,
                    projectName: appointment.xiangmumingcheng || '未知项目',
                    customerName: appointment.xingming,
                    countdown: countdown,
                    remark: appointment.yuyueshuoming
                  };
                  
                  console.log(`教练${coachId}待上课, ${startTime.toISOString()} - ${endTime.toISOString()}, 倒计时: ${countdown}分钟`);
                }
              } else {
                console.warn(`教练${coachId}的预约时间格式无效:`, startTimeStr, endTimeStr);
              }
            } catch (e) {
              console.error('解析预约时间出错:', e, appointment);
            }
          });
        }
        
        // 处理支付记录数据
        if (paymentsResponse.data && paymentsResponse.data.code === 0) {
          const payments = paymentsResponse.data.data?.list || [];
          console.log('获取到的支付记录:', payments);
          
          payments.forEach(payment => {
            if (!payment.coachId) return;
            
            // 尝试从映射中获取正确的教练ID
            const rawCoachId = payment.coachId;
            const coachId = coachIdMap.get(rawCoachId) || (typeof rawCoachId === 'string' ? rawCoachId : rawCoachId.toString());
            
            // 输出调试信息
            console.log(`支付记录中的教练ID: ${rawCoachId} (${typeof rawCoachId}), 映射后: ${coachId}`);
            console.log(`支付记录的时间: 开始=${payment.startTime}, 结束=${payment.endTime}`);
            
            // 检查支付记录中的服务时间是否在当前时间点
            try {
              if (payment.startTime && payment.endTime) {
                // 确保时间字符串正确解析
                let startTime, endTime;
                
                // 提供更详细的时间解析日志
                console.log(`解析支付记录时间 - 原始开始时间: "${payment.startTime}", 原始结束时间: "${payment.endTime}"`);
                
                // 尝试解析时间，处理多种可能的格式
                try {
                  startTime = new Date(payment.startTime);
                  console.log(`解析开始时间结果: ${startTime}, 有效? ${!isNaN(startTime.getTime())}`);
                  
                  if (isNaN(startTime.getTime())) {
                    // 如果解析失败，尝试其他格式
                    if (typeof payment.startTime === 'string') {
                      // 处理可能的yyyy-MM-dd格式，添加时间
                      if (payment.startTime.length === 10) {
                        startTime = new Date(`${payment.startTime}T00:00:00`);
                        console.log(`尝试另一种格式解析开始时间: ${startTime}, 有效? ${!isNaN(startTime.getTime())}`);
                      }
                    }
                  }
                  
                  endTime = new Date(payment.endTime);
                  console.log(`解析结束时间结果: ${endTime}, 有效? ${!isNaN(endTime.getTime())}`);
                  
                  if (isNaN(endTime.getTime())) {
                    // 如果解析失败，尝试其他格式
                    if (typeof payment.endTime === 'string') {
                      // 处理可能的yyyy-MM-dd格式，添加时间
                      if (payment.endTime.length === 10) {
                        endTime = new Date(`${payment.endTime}T23:59:59`);
                        console.log(`尝试另一种格式解析结束时间: ${endTime}, 有效? ${!isNaN(endTime.getTime())}`);
                      }
                    }
                  }
                } catch (e) {
                  console.error('时间解析错误:', e);
                  return;
                }
                
                if (!isNaN(startTime.getTime()) && !isNaN(endTime.getTime())) {
                  console.log(`服务时间比较 - 开始<=现在? ${startTime <= now}, 结束>=现在? ${endTime >= now}`);
                  console.log(`验证时间范围: ${startTime.toISOString()} <= ${now.toISOString()} <= ${endTime.toISOString()}? ${startTime <= now && endTime >= now}`);
                  
                  // 如果当前时间在服务时间范围内，标记为"服务中"
                  if (startTime <= now && endTime >= now) {
                    // 优先级：服务中 > 预约中
                    statusMap[coachId] = '服务中';
                    
                    // 记录当前服务信息
                    activityMap[coachId] = {
                      type: '服务',
                      startTime: startTime,
                      endTime: endTime,
                      projectName: payment.projectName || payment.customProjectName || '未知项目',
                      customerName: payment.customerName || payment.memberAccount,
                      remark: payment.remark
                    };
                    
                    console.log(`教练${coachId}服务中, ${startTime.toISOString()} - ${endTime.toISOString()}`);
                  } 
                  // 检查是否将在30分钟内开始服务
                  else if (startTime > now && (startTime.getTime() - now.getTime()) < 30 * 60 * 1000) {
                    // 仅当没有更高优先级状态时才设置为"待服务"
                    if (statusMap[coachId] !== '服务中' && statusMap[coachId] !== '预约中') {
                      statusMap[coachId] = '待服务';
                      
                      // 记录即将开始的服务信息
                      const countdown = Math.floor((startTime.getTime() - now.getTime()) / 60000);
                      activityMap[coachId] = {
                        type: '服务',
                        startTime: startTime,
                        endTime: endTime,
                        projectName: payment.projectName || payment.customProjectName || '未知项目',
                        customerName: payment.customerName || payment.memberAccount,
                        countdown: countdown,
                        remark: payment.remark
                      };
                      
                      console.log(`教练${coachId}待服务, ${startTime.toISOString()} - ${endTime.toISOString()}, 倒计时: ${countdown}分钟`);
                    }
                  }
                } else {
                  console.warn(`教练${coachId}的服务时间格式无效:`, payment.startTime, payment.endTime);
                }
              }
            } catch (e) {
              console.error('解析服务时间出错:', e, payment);
            }
          });
        }
        
        // 处理空闲时间数据
        if (availabilityResponse.data && availabilityResponse.data.code === 0) {
          const availabilities = availabilityResponse.data.data || [];
          
          // 为没有空闲时间的教练设置状态
          this.coachesList.forEach(coach => {
            const id = coach.id;
            
            const hasAvailability = availabilities.some(
              slot => {
                // 考虑不同格式的coachId
                const slotCoachId = slot.coachId;
                return coachIdMap.get(slotCoachId) === id && new Date(slot.availableUntil) > now;
              }
            );
            
            // 只有当教练当前状态为"空闲"且没有空闲时间时，才设置为"休息"
            if (!hasAvailability && statusMap[id] === '空闲') {
              statusMap[id] = '休息';
              console.log(`教练${id}休息中`);
            }
          });
        }
        
        // 更新教练列表中的状态
        this.updateCoachesWithStatus(statusMap, activityMap);
        
        // 记录状态映射到控制台，用于调试
        console.log('教练状态映射最终结果:', statusMap);
        console.log('教练活动信息最终结果:', activityMap);
      })
      .catch(error => {
        console.error('获取教练状态数据错误:', error);
        // 使用随机状态
        this.coachesList.forEach(coach => {
          statusMap[coach.id] = this.getRandomStatus();
        });
        this.updateCoachesWithStatus(statusMap);
      });
    },
    
    // 更新教练列表的状态
    updateCoachesWithStatus(statusMap, activityMap = {}) {
      this.coachesList = this.coachesList.map(coach => ({
        ...coach,
        status: statusMap[coach.id] || '空闲'
      }));
      
      // 更新教练活动信息
      this.coachCurrentActivities = activityMap || {};
      
      this.filterByStatus(); // 更新筛选结果
    },
    
    // 更新每个教练的空闲时间数量
    updateCoachesAvailabilityCounts() {
      // 获取所有教练的空闲时间数据
      this.$http.get('/schedule/getAllAvailability')
        .then(response => {
          if (response.data && response.data.code === 0) {
            // 修复API返回结构，确保正确获取数据
            const allAvailability = response.data.data || [];
            console.log('获取到的空闲时间数据:', allAvailability);
            
            // 计算每个教练的空闲时间数量
            const countMap = {};
            allAvailability.forEach(slot => {
              if (!countMap[slot.coachId]) {
                countMap[slot.coachId] = 0;
              }
              countMap[slot.coachId]++;
            });
            
            // 更新教练列表中的空闲时间数量
            this.coachesList = this.coachesList.map(coach => ({
              ...coach,
              availabilityCount: countMap[coach.id] || 0
            }));
            
            this.filterByStatus(); // 更新筛选结果
          } else {
            console.error('获取空闲时间数据失败:', response.data?.msg);
          }
        })
        .catch(error => {
          console.error('获取空闲时间数量错误:', error);
        });
    },
    
    // 生成随机状态（仅在API失败时使用备用）
    getRandomStatus() {
      const statuses = ['空闲', '预约中', '服务中', '待上课', '待服务', '休息'];
      return statuses[Math.floor(Math.random() * statuses.length)];
    },
    
    // 根据状态筛选教练
    filterByStatus() {
      if (this.statusFilter === '全部') {
        this.filteredCoaches = [...this.coachesList];
      } else {
        this.filteredCoaches = this.coachesList.filter(coach => coach.status === this.statusFilter);
      }
    },
    
    // 搜索教练
    searchCoaches() {
      if (!this.searchInput.trim()) {
        this.filterByStatus();
        return;
      }
      
      const query = this.searchInput.toLowerCase().trim();
      this.filteredCoaches = this.coachesList.filter(coach => 
        coach.id.toLowerCase().includes(query) || 
        coach.name.toLowerCase().includes(query)
      );
    },
    
    // 刷新教练列表
    refreshCoaches() {
      this.searchInput = '';
      this.fetchCoaches();
    },
    
    // 选择教练，显示详情
    selectCoach(coach) {
      this.selectedCoach = coach;
      this.showTimeDetailsDialog = true;
      this.fetchCoachTimeSlots(coach.id);
    },
    
    // 获取教练的空闲时间段
    fetchCoachTimeSlots(coachId) {
      this.timeSlotsLoading = true;
      
      // 如果是测试数据，生成模拟的时间段数据
      if (this.selectedCoach.isTestData) {
        setTimeout(() => {
          this.generateTestTimeSlots();
          this.timeSlotsLoading = false;
        }, 500); // 模拟网络延迟
        return;
      }
      
      this.$http.get(`/schedule/${coachId}/availability`)
        .then(response => {
          if (response.data && response.data.code === 0) {
            this.coachTimeSlots = response.data.data || [];
            
            if (this.coachTimeSlots.length === 0) {
              this.$message.info(`${this.selectedCoach.name}暂无空闲时间段`);
            }
          } else {
            this.$message.error('获取空闲时间失败：' + (response.data?.msg || '未知错误'));
            this.coachTimeSlots = [];
            // 生成测试数据
            this.generateTestTimeSlots();
          }
        })
        .catch(error => {
          console.error('获取教练空闲时间错误:', error);
          this.$message.error('获取空闲时间出错：' + (error.message || '网络错误'));
          this.coachTimeSlots = [];
          // 生成测试数据
          this.generateTestTimeSlots();
        })
        .finally(() => {
          this.timeSlotsLoading = false;
        });
    },
    
    // 生成测试时间段数据
    generateTestTimeSlots() {
      const today = new Date();
      const slots = [];
      
      // 生成未来7天的随机时间段
      for (let i = 0; i < 7; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() + i);
        
        // 每天生成1-3个时间段
        const slotsPerDay = Math.floor(Math.random() * 3) + 1;
        
        for (let j = 0; j < slotsPerDay; j++) {
          // 随机开始小时 (8-20)
          const startHour = 8 + Math.floor(Math.random() * 12);
          // 随机持续1-3小时
          const duration = Math.floor(Math.random() * 3) + 1;
          
          const fromDate = new Date(date);
          fromDate.setHours(startHour, 0, 0);
          
          const toDate = new Date(fromDate);
          toDate.setHours(fromDate.getHours() + duration);
          
          slots.push({
            id: `test-${i}-${j}`,
            coachId: this.selectedCoach.id,
            availableFrom: fromDate.toISOString(),
            availableUntil: toDate.toISOString(),
            isTestData: true
          });
        }
      }
      
      this.coachTimeSlots = slots;
    },
    
    // 为选中的教练添加空闲时间
    addTimeForCoach() {
      this.isEditing = false;
      this.form = this.initFormData();
      this.form.coachId = this.selectedCoach.id;
      this.showAddDialog = true;
    },
    
    // 编辑时间段
    editTimeSlot(timeSlot) {
      this.isEditing = true;
      // 确保时间字符串被正确解析为Date对象
      const fromDate = new Date(timeSlot.availableFrom);
      const toDate = new Date(timeSlot.availableUntil);
      
      this.form = {
        id: timeSlot.id,
        coachId: timeSlot.coachId,
        startDateTime: this.formatDateTime(fromDate),
        endDateTime: this.formatDateTime(toDate),
      };
      
      this.showAddDialog = true;
    },
    
    // 删除时间段
    deleteTimeSlot(id) {
      this.$confirm('确认删除该空闲时间段？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const queryString = `?id=${encodeURIComponent(id)}`;
        this.$http.post(`/schedule/deleteAvailability${queryString}`)
          .then(response => {
            if (response.data && response.data.code === 0) {
              this.$message.success('删除成功');
              this.fetchCoachTimeSlots(this.selectedCoach.id);
              this.updateCoachesAvailabilityCounts();
            } else {
              this.$message.error(response.data.msg || '删除失败');
            }
          })
          .catch(error => {
            console.error('删除空闲时间错误:', error);
            this.$message.error('删除出错');
          });
      }).catch(() => {});
    },
    
    // 保存空闲时间
    saveAvailability() {
      this.$refs.form.validate(valid => {
        if (!valid) return;
        
        // 验证时间范围
        const startDate = new Date(this.form.startDateTime);
        const endDate = new Date(this.form.endDateTime);
        
        if (startDate >= endDate) {
          this.$message.error('结束时间必须晚于开始时间');
          return;
        }
        
        this.submitting = true;
        
        // 构建请求参数
        let endpoint, queryString;
        if (this.isEditing) {
          queryString = `?id=${encodeURIComponent(this.form.id)}`
            + `&availableFrom=${encodeURIComponent(this.form.startDateTime)}`
            + `&availableUntil=${encodeURIComponent(this.form.endDateTime)}`;
          endpoint = `/schedule/updateAvailabilityById${queryString}`;
        } else {
          queryString = `?jiaolianbianhao=${encodeURIComponent(this.form.coachId)}`
            + `&availableFrom=${encodeURIComponent(this.form.startDateTime)}`
            + `&availableUntil=${encodeURIComponent(this.form.endDateTime)}`;
          endpoint = `/schedule/setAvailability${queryString}`;
        }
        
        this.$http.post(endpoint)
          .then(response => {
            if (response.data && response.data.code === 0) {
              this.$message.success(this.isEditing ? '更新成功' : '添加成功');
              this.showAddDialog = false;
              
              // 如果当前有选中的教练，刷新其时间段
              if (this.selectedCoach.id) {
                this.fetchCoachTimeSlots(this.selectedCoach.id);
              }
              
              this.updateCoachesAvailabilityCounts();
            } else {
              this.$message.error(response.data.msg || (this.isEditing ? '更新失败' : '添加失败'));
            }
          })
          .catch(error => {
            console.error('保存空闲时间错误:', error);
            this.$message.error('操作失败');
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
    
    // 快速设置时间范围
    setTimeRange(range) {
      const [start, end] = range.split('-');
      const [startHour, startMinute] = start.split(':').map(Number);
      const [endHour, endMinute] = end.split(':').map(Number);
      
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), startHour, startMinute);
      const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), endHour, endMinute);
      
      // 使用formatDateTime确保输出格式正确
      this.form.startDateTime = this.formatDateTime(startDate);
      this.form.endDateTime = this.formatDateTime(endDate);
    },
    
    // 获取特定日期的时间段
    getTimeSlotsForDate(date) {
      const dateStr = this.formatDate(date);
      return this.coachTimeSlots.filter(slot => 
        this.formatDate(new Date(slot.availableFrom)) === dateStr
      );
    },
    
    // 检查时间段是否已预约
    isTimeSlotBooked(timeSlot) {
      // 实际应用中应该根据后端数据判断
      return false;
    },
    
    // 获取时间段状态
    getSlotStatus(timeSlot) {
      if (this.isTimeSlotBooked(timeSlot)) {
        return '已预约';
      }
      const now = new Date();
      const from = new Date(timeSlot.availableFrom);
      const until = new Date(timeSlot.availableUntil);
      
      if (now > until) {
        // return '已过期';
                return this.$t('vipUser.expired')
      } else if (now >= from && now <= until) {
        // return '进行中';
                return this.$t('roomcleaning.inProgress')
      } else {
        // return '可预约';
        return this.$t('coachSchedule.canBook')
      }
    },
    
    // 获取时间段状态对应的类型
    getSlotStatusType(timeSlot) {
      const status = this.getSlotStatus(timeSlot);
      switch (status) {
        case '已预约': return 'primary';
        case this.$t('vipUser.expired'): return 'info';
        case this.$t('roomcleaning.inProgress'): return 'warning';
        case this.$t('coachSchedule.canBook'): return 'success';
        default: return '';
      }
    },
    
    // 分页相关方法
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchCoaches();
    },
    
    handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.fetchCoaches();
    },
    
    // 格式化方法
    formatDate(date) {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    
    formatTime(date) {
      const d = new Date(date);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    },
    
    formatDateTime(date) {
      if (!date) return '';
      // 确保date是Date对象
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return '';
      
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    
    formatTimeSlot(slot) {
      return `${this.formatTime(new Date(slot.availableFrom))} - ${this.formatTime(new Date(slot.availableUntil))}`;
    },

    // 图片加载错误处理
    handleImageError(e) {
      e.target.src = this.defaultAvatar;
    },

    // 更新教练卡片样式，确保正确显示状态颜色
    getStatusClass(status) {
      switch(status) {
        case '空闲': return 'status-free';
        case '预约中': return 'status-booked';
        case '服务中': return 'status-serving';
        case '待上课': return 'status-pending';
        case '待服务': return 'status-waiting';
        case '休息': return 'status-rest';
        default: return '';
      }
    },

    // 添加获取活动标题的方法
    getActivityTitle(status) {
      switch (status) {
        case '预约中': return '当前预约';
        case '服务中': return '当前服务';
        case '待上课': return '即将开始的预约';
        case '待服务': return '即将开始的服务';
        default: return '活动信息';
      }
    },

    // 添加获取活动提醒类型的方法
    getActivityAlertType(status) {
      switch (status) {
        case '预约中': return 'info';
        case '服务中': return 'warning';
        case '待上课': return 'success';
        case '待服务': return 'success';
        default: return 'info';
      }
    },

    // 添加获取状态标签类型的方法
    getStatusTagType(status) {
      switch (status) {
        case '空闲': return 'success';
        case '预约中': return 'info';
        case '服务中': return 'warning';
        case '待上课': return 'primary';
        case '待服务': return 'primary';
        case '休息': return 'info';
        default: return '';
      }
    },

    // 打开预约对话框
    openBookDialog() {
      // 设置当前选中教练
      this.bookForm.coachId = this.selectedCoach.id;
      this.showBookDialog = true;
      this.getProjects();
    },
    
    // 获取健身项目列表
    getProjects() {
      this.$http({
        url: '/jianshenxiangmu/list',
        method: 'get',
        params: {
          page: 1,
          limit: 100
        }
      }).then(({data}) => {
        if (data && data.code === 0) {
          this.projects = data.data.list || [];
        }
      });
    },
    
    // 根据客户类型变更表单验证规则
    customerTypeChange(val) {
      if (val === 1) {
        this.$refs.bookForm.clearValidate('memberId');
      } else {
        this.$refs.bookForm.clearValidate(['name', 'phone']);
      }
    },
    
    // 搜索会员
    searchMembers() {
      if (!this.searchKey) {
        this.$message.warning('请输入搜索关键词');
        return;
      }
      
      this.$http({
        url: '/yonghu/search',
        method: 'get',
        params: {
          keyword: this.searchKey
        }
      }).then(({data}) => {
        if (data && data.code === 0) {
          this.members = data.data || [];
          if (this.members.length === 0) {
            this.$message.info('未找到相关会员信息');
          }
        } else {
          this.$message.error('搜索会员失败');
        }
      });
    },
    
    // 选择会员
    selectMember(row) {
      this.selectedMember = row;
      this.bookForm.memberId = row.id;
      this.bookForm.name = row.xingming;
      this.bookForm.phone = row.shouji;
    },
    
    // 项目改变时
    projectChange(projectId) {
      if (projectId !== 'other') {
        const selectedProject = this.projects.find(p => p.id === projectId);
        if (selectedProject) {
          // 设置服务时长
          if (selectedProject.shixiangshijian) {
            this.bookForm.duration = parseInt(selectedProject.shixiangshijian) || 60;
          }
          
          // 设置价格
          if (selectedProject.jiage) {
            this.bookForm.price = parseFloat(selectedProject.jiage) || 0;
          }
          
          this.updateEndTime();
        }
      }
    },
    
    // 更新结束时间
    updateEndTime() {
      if (this.bookForm.startTime && this.bookForm.duration) {
        const startTime = new Date(this.bookForm.startTime);
        const endTime = new Date(startTime.getTime() + this.bookForm.duration * 60000);
        // 格式化为yyyy-MM-dd HH:mm:ss格式
        this.bookForm.endTime = this.formatDateTime(endTime);
      }
    },
    
    // 提交预约
    submitBooking() {
      this.$refs.bookForm.validate(valid => {
        if (valid) {
          // 构建提交数据
          const submitData = {
            jiaolianbianhao: this.selectedCoach.id,
            jiaolianxingming: this.selectedCoach.name,
            startTime: this.bookForm.startTime,
            endTime: this.bookForm.endTime,
            yuyueshuoming: this.bookForm.remark,
            sfsh: '是', // 修改为已审核通过
            shhf: '系统自动审核通过', // 添加审核回复
            xiangmuid: this.bookForm.projectId === 'other' ? null : this.bookForm.projectId,
            projectId: this.bookForm.projectId === 'other' ? null : this.bookForm.projectId,
            xiangmumingcheng: this.bookForm.projectId === 'other' ? this.bookForm.customProjectName : 
              this.projects.find(p => p.id === this.bookForm.projectId)?.xiangmumingcheng || '',
            duration: this.bookForm.duration,
            price: this.bookForm.price,
            paymentMethod: this.bookForm.paymentMethod, // 支付方式
            paymentStatus: 1, // 添加支付状态为已付款(1)
            status: '已付款' // 添加状态为已付款
          };
          
          // 根据客户类型添加不同参数
          if (this.bookForm.customerType === 1) {
            // 散客
            submitData.xingming = this.bookForm.name;
            submitData.shouji = this.bookForm.phone;
          } else {
            // 会员
            submitData.zhanghao = this.selectedMember.zhanghao;
            submitData.xingming = this.selectedMember.xingming;
            submitData.shouji = this.selectedMember.shouji;
          }
          
          this.$http({
            url: '/jiaolianyuyue/add',
            method: 'post',
            data: submitData
          }).then(({data}) => {
            if (data && data.code === 0) {
              this.$message.success('预约提交成功，等待教练确认');
              this.showBookDialog = false;
              this.resetBookForm();
              this.refreshCoaches(); // 刷新教练列表以反映变化
            } else {
              this.$message.error(data.msg || '预约提交失败');
            }
          }).catch(error => {
            console.error('预约提交出错:', error);
            this.$message.error('预约提交出错，请检查网络连接或联系管理员');
          });
        } else {
          this.$message.warning('请完善表单信息');
          return false;
        }
      });
    },
    
    // 重置预约表单
    resetBookForm() {
      if(this.$refs.bookForm) {
        this.$refs.bookForm.resetFields();
      }
      this.bookForm = {
        customerType: 1,
        name: '',
        phone: '',
        memberId: null,
        projectId: null,
        customProjectName: '',
        duration: 60,
        price: 0,
        remark: '',
        startTime: null,
        endTime: null,
        paymentMethod: 1 // 重置支付方式为默认值（现金）
      };
      this.selectedMember = null;
      this.members = [];
      this.searchKey = '';
    },
  }
};
</script>

<style scoped>
.availability-page {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.status-filter {
  margin-left: 20px;
}

.test-data-alert {
  margin-bottom: 20px;
}

.actions {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.search-box {
  display: flex;
  align-items: center;
  position: unset !important;
}

.search-input {
  width: 250px;
  margin-right: 10px;
}

.coach-cards {
  margin-bottom: 30px;
  min-height: 300px;
}

.coach-card {
  margin-bottom: 20px;
  transition: all 0.3s;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  height: 220px;
}

.coach-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.coach-card.test-data {
  border: 1px dashed #e6a23c;
  background-color: #fdf6ec;
}

/* 卡片内容布局 */
.card-content {
  display: flex;
  height: 100%;
}

.card-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-right: 10px;
  border-right: 1px solid #ebeef5;
}

.card-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 15px;
  overflow: hidden;
}

.empty-activity {
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-activity {
  text-align: center;
  color: #909399;
}

.no-activity i {
  font-size: 24px;
  margin-bottom: 5px;
}

.coach-avatar {
  text-align: center;
  position: relative;
  margin-bottom: 10px;
}

.coach-avatar img {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #eee;
}

.status-badge {
  position: absolute;
  top: 0;
  right: 0;
  padding: 4px 8px;
  border-radius: 10px;
  font-size: 12px;
  color: white;
}

.test-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 12px;
  background-color: #e6a23c;
  color: white;
}

/* 状态标签颜色 */
.status-空闲 {
  background-color: #67C23A;
}

.status-预约中 {
  background-color: #409EFF;
}

.status-服务中 {
  background-color: #E6A23C;
}

.status-待上课 {
  background-color: #F56C6C;
}

.status-待服务 {
  background-color: #909399;
}

.status-休息 {
  background-color: #909399;
}

/* 状态卡片边框颜色 */
.status-free {
  border-top: 3px solid #67C23A;
}

.status-booked {
  border-top: 3px solid #409EFF;
}

.status-serving {
  border-top: 3px solid #E6A23C;
}

.status-pending {
  border-top: 3px solid #F56C6C;
}

.status-waiting {
  border-top: 3px solid #909399;
}

.status-rest {
  border-top: 3px solid #909399;
}

.coach-info {
  text-align: center;
  width: 100%;
}

.coach-name {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
}

.coach-id, .coach-level {
  font-size: 13px;
  color: #606266;
  margin-bottom: 5px;
}

.availability-info {
  font-size: 13px;
  color: #409EFF;
  margin-top: 10px;
}

/* 活动信息相关的样式 */
.activity-info {
  width: 100%;
}

.activity-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.activity-title {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
}

.activity-details {
  font-size: 12px;
  color: #666;
  overflow: hidden;
}

.activity-item {
  margin: 8px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.countdown {
  color: #F56C6C;
  font-weight: bold;
}

.activity-details .el-icon-time,
.activity-details .el-icon-s-order,
.activity-details .el-icon-alarm-clock {
  margin-right: 5px;
  color: #409EFF;
}

.current-activity-panel {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.activity-details-table {
  margin-top: 15px;
  font-size: 14px;
}

/* 修改活动信息在不同状态下的颜色 */
.status-booked .activity-title {
  color: #409EFF;
}

.status-serving .activity-title {
  color: #E6A23C;
}

.status-pending .activity-title {
  color: #F56C6C;
}

.status-waiting .activity-title {
  color: #909399;
}

/* 增加指示器样式，显示计时效果 */
.countdown-indicator {
  display: inline-block;
  margin-left: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #F56C6C;
  animation: blink 1s infinite;
}

@keyframes blink {
  0% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 0.3; }
}

.time-management-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.coach-basic-info {
  display: flex;
  align-items: center;
}

.dialog-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 15px;
  object-fit: cover;
}

.calendar-view {
  margin-top: 20px;
}

.calendar-header {
  margin-bottom: 15px;
  display: flex;
  justify-content: flex-end;
}

.full-calendar {
  height: 500px;
  overflow-y: auto;
}

.calendar-date-cell {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.calendar-day {
  text-align: right;
  padding: 5px;
  font-weight: bold;
}

.time-slots {
  flex: 1;
  overflow-y: auto;
  font-size: 12px;
}

.time-slot {
  padding: 2px 5px;
  margin-bottom: 2px;
  background-color: #ecf5ff;
  border-radius: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.time-slot.test-data {
  background-color: #fdf6ec;
  border: 1px dashed #e6a23c;
}

.time-list {
  max-height: 400px;
  overflow-y: auto;
}

.quick-time-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.pagination {
  margin-top: 20px;
  text-align: center;
}

.coach-actions {
  display: flex;
  gap: 10px;
}

.selected-member {
  margin-top: 10px;
  margin-bottom: 20px;
}

.unit {
  margin-left: 5px;
}

@media (max-width: 768px) {
  .page-header, .actions {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .status-filter, .search-box {
    margin-top: 10px;
    margin-left: 0;
  }
  
  .search-input {
    width: 100%;
    margin-right: 0;
    margin-bottom: 10px;
  }
  
  .actions {
    flex-direction: column;
  }
  
  .actions .el-button {
    margin-bottom: 10px;
  }
  
  .card-content {
    flex-direction: column;
  }
  
  .card-left {
    border-right: none;
    border-bottom: 1px solid #ebeef5;
    padding-right: 0;
    padding-bottom: 10px;
  }
  
  .card-right {
    padding-left: 0;
    padding-top: 10px;
  }
  
  .coach-card {
    height: auto;
  }
}
</style>
